import { jsx, ThemeProvider } from 'theme-ui';
import { MDXProvider } from '@mdx-js/react';
import React, { forwardRef, useState } from 'react';
import { Global } from '@emotion/react';
import merge from 'deepmerge';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var createNestedLinks = function createNestedLinks(children, depth) {
  if (depth === void 0) {
    depth = 0;
  }

  return React.Children.toArray(children).reduce(function (acc, child) {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return acc;
    }

    var type = child.props && child.props.mdxType;
    if (!child.props || !child.props.children) return acc;
    if (type === 'a') return [].concat(acc, [child]);

    if (depth > 0 && type === 'ul') {
      var last = acc[acc.length - 1];
      acc[acc.length - 1] = /*#__PURE__*/React.cloneElement(last, {
        links: createNestedLinks(child.props.children)
      });
      return acc;
    }

    return [].concat(acc, createNestedLinks(child.props.children, depth + 1));
  }, []);
};

var flattenLinks = function flattenLinks(children) {
  return React.Children.toArray(children).reduce(function (acc, child) {
    if ( /*#__PURE__*/React.isValidElement(child)) {
      if (child.props && child.props.mdxType === 'a') {
        return [].concat(acc, [child]);
      }

      if (!child.props || !child.props.children) return acc;
      return React.Children.toArray([].concat(acc, flattenLinks(child.props.children)));
    }

    return acc;
  }, []);
};

var Overlay = function Overlay(_ref) {
  var onClick = _ref.onClick;
  return jsx(React.Fragment, null, jsx("div", {
    onClick: onClick,
    sx: {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    }
  }), jsx(Global, {
    styles: {
      body: {
        overflow: ['hidden', 'auto']
      }
    }
  }));
};

var Sidenav = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var open = _ref2.open,
      _ref2$styles = _ref2.styles,
      styles = _ref2$styles === void 0 ? {} : _ref2$styles,
      _ref2$components = _ref2.components,
      components = _ref2$components === void 0 ? {} : _ref2$components,
      props = _objectWithoutPropertiesLoose(_ref2, ["open", "styles", "components"]);

  return jsx(ThemeProvider, {
    theme: {
      styles: merge({
        ul: {
          listStyle: 'none',
          p: 0,
          m: 0,
          ul: {
            a: {
              pl: 4
            }
          }
        },
        a: {
          display: 'block',
          px: 2,
          py: 2,
          color: 'inherit',
          textDecoration: 'none',
          fontSize: 1,
          fontWeight: 'bold'
        }
      }, styles)
    }
  }, open && jsx(Overlay, Object.assign({}, props)), jsx(MDXProvider, {
    components: components
  }, jsx("div", Object.assign({}, props, {
    ref: ref,
    sx: {
      position: ['fixed', 'sticky'],
      top: 0,
      left: 0,
      bottom: [0, 'auto'],
      zIndex: 1,
      minWidth: 0,
      width: 256,
      maxHeight: ['100vh', 'none'],
      overflowX: 'visible',
      overflowY: 'auto',
      transition: 'transform .2s ease-out',
      transform: [open ? 'translateX(0)' : 'translate(-100%)', 'none'],
      bg: ['background', 'transparent'],
      WebkitOverflowScrolling: 'touch'
    }
  }))));
});

var AccordionButton = function AccordionButton(props) {
  var transform = props.open ? 'rotate(-180 8 8)' : 'rotate(0 8 8)';
  var disabled = props.pathname ? props.pathname.includes(props.href) : false;
  return jsx("button", Object.assign({
    title: "Expand Section",
    disabled: disabled
  }, props, {
    onMouseDown: function onMouseDown(e) {
      return e.preventDefault();
    },
    sx: {
      appearance: 'none',
      display: 'flex',
      alignItems: 'center',
      p: 2,
      m: 0,
      border: 0,
      borderRadius: 0,
      color: 'inherit',
      bg: 'transparent',
      ':hover,:focus': {
        color: 'primary'
      },
      '&:disabled': {
        opacity: 0.25
      }
    }
  }), jsx("svg", {
    viewBox: "0 0 16 16",
    width: "12",
    height: "12"
  }, jsx("g", {
    sx: {
      transformOrigin: '8 8',
      transition: 'transform .1s ease-out'
    },
    transform: transform
  }, jsx("path", {
    stroke: "currentcolor",
    strokeWidth: "2",
    fill: "none",
    d: "M14 6 L8 12 L2 6"
  }))));
};

var NavLinks = function NavLinks(_ref3) {
  var open = _ref3.open,
      _ref3$pathname = _ref3.pathname,
      pathname = _ref3$pathname === void 0 ? '' : _ref3$pathname,
      links = _ref3.links,
      href = _ref3.href,
      Link = _ref3.Link;
  if (!links) return null;
  if (!open && !pathname.includes(href)) return null;
  return jsx("ul", {
    sx: {
      listStyle: 'none',
      m: 0,
      p: 0
    }
  }, links.map(function (link, j) {
    return jsx("li", {
      key: j
    }, jsx(Link, {
      href: link.props.href,
      children: link.props.children,
      className: link.props.className,
      sx: {
        pl: 4
      }
    }));
  }));
};

var AccordionNav = /*#__PURE__*/forwardRef(function (_ref4, ref) {
  var open = _ref4.open,
      children = _ref4.children,
      _ref4$components = _ref4.components,
      components = _ref4$components === void 0 ? {} : _ref4$components,
      className = _ref4.className,
      _ref4$pathname = _ref4.pathname,
      pathname = _ref4$pathname === void 0 ? '' : _ref4$pathname,
      props = _objectWithoutPropertiesLoose(_ref4, ["open", "children", "components", "className", "pathname"]);

  var links = createNestedLinks(children);

  var _useState = useState({}),
      expanded = _useState[0],
      setExpanded = _useState[1];

  var Link = components.a || 'a';

  var toggle = function toggle(i) {
    return function (e) {
      var _extends2;

      e.stopPropagation();
      setExpanded(_extends({}, expanded, (_extends2 = {}, _extends2[i] = !expanded[i], _extends2)));
    };
  };

  return jsx(React.Fragment, null, open && jsx(Overlay, Object.assign({}, props)), jsx("div", {
    ref: ref,
    className: className,
    sx: {
      position: ['fixed', 'sticky'],
      top: 0,
      left: 0,
      bottom: [0, 'auto'],
      zIndex: 1,
      minWidth: 0,
      width: 256,
      maxHeight: ['100vh', 'none'],
      overflowX: 'visible',
      overflowY: 'auto',
      transition: 'transform .2s ease-out',
      transform: [open ? 'translateX(0)' : 'translate(-100%)', 'none'],
      bg: ['background', 'transparent'],
      WebkitOverflowScrolling: 'touch'
    }
  }, jsx("ul", {
    sx: {
      listStyle: 'none',
      p: 0,
      m: 0
    }
  }, links.map(function (link, i) {
    return jsx("li", {
      key: i
    }, jsx("div", {
      sx: {
        display: 'flex',
        alignItems: 'center'
      }
    }, jsx(Link, {
      href: link.props.href,
      children: link.props.children,
      className: link.props.className
    }), link.props.links && jsx(AccordionButton, {
      href: link.props.href,
      pathname: pathname,
      open: expanded[i],
      sx: {
        ml: 'auto'
      },
      onClick: toggle(i)
    })), jsx(NavLinks, Object.assign({}, link.props, {
      open: expanded[i],
      pathname: pathname,
      Link: Link
    })));
  }))));
});

var removeSlash = function removeSlash(str) {
  return str.length > 1 ? str.replace(/\/$/, '') : str;
};

var PaginationLink = function PaginationLink(_ref5) {
  var label = _ref5.label,
      children = _ref5.children,
      props = _objectWithoutPropertiesLoose(_ref5, ["label", "children", "mdxType", "originalType", "parentName"]);

  return jsx("a", Object.assign({}, props, {
    sx: {
      fontSize: 1,
      color: 'inherit',
      textDecoration: 'none',
      fontWeight: 'bold'
    }
  }), jsx("div", null, label), jsx("div", {
    sx: {
      fontSize: 3
    }
  }, children));
};

var Pagination = function Pagination(_ref6) {
  var _ref6$pathname = _ref6.pathname,
      pathname = _ref6$pathname === void 0 ? '' : _ref6$pathname,
      children = _ref6.children,
      props = _objectWithoutPropertiesLoose(_ref6, ["pathname", "children"]);

  var links = flattenLinks(children);
  var index = links.findIndex(function (link) {
    return /*#__PURE__*/React.isValidElement(link) && link.props.href === removeSlash(pathname);
  });
  var hasPagination = index > -1;
  var previous = links[index - 1];
  var next = links[index + 1];
  return jsx("div", Object.assign({}, props, {
    sx: {
      display: 'flex'
    }
  }), hasPagination && previous && /*#__PURE__*/React.isValidElement(previous) && jsx(PaginationLink, Object.assign({}, previous.props, {
    label: "Previous:"
  })), jsx("div", {
    sx: {
      mx: 'auto'
    }
  }), hasPagination && next && /*#__PURE__*/React.isValidElement(next) && jsx(PaginationLink, Object.assign({}, next.props, {
    label: "Next:"
  })));
};

export { AccordionButton, AccordionNav, Pagination, Sidenav };